import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { ConstructionDiaryEntity } from '../../entities/construction-diary.entity';
import { getLatestUpdatedAt } from '../../helper/reducer.helper';
import { ConstructionDiaryActionTypes } from '../actions/construction-diary.actions';

export interface ConstructionDiaryState extends EntityState<ConstructionDiaryEntity> {
    fetchedByCommissionId: { [commissionId: number]: boolean };
    latestUpdatedAtByCommissionId: { [commissionId: number]: Date | null }
}

export const CONSTRUCTION_DIARY_KEY = 'constructionDiary';

const adapter = createEntityAdapter<ConstructionDiaryEntity>({
    selectId: (Entity) => Entity.Id,
});

const initialState = adapter.getInitialState({
    fetchedByCommissionId: {},
    latestUpdatedAtByCommissionId: {},
});

/* ############# */
/* ## REDUCER ## */
/* ############# */

export const constructionDiaryReducer = createReducer(
    initialState,
    on(ConstructionDiaryActionTypes.UpdateAll, (state, { Payload, commissionId, updateLatestUpdatedAt }) => {
        const idsToRemove = Object.values(state.entities)
            .filter((entity): entity is ConstructionDiaryEntity => !!entity && entity.CommissionId === commissionId)
            .map((entity) => entity.Id);
        const cleanedState = adapter.removeMany(idsToRemove, state);
        const newLatestUpdatedAt = updateLatestUpdatedAt ? getLatestUpdatedAt(Payload, null) : state.latestUpdatedAtByCommissionId[commissionId];
        return adapter.addMany(Payload, {
            ...cleanedState,
            fetchedByCommissionId: { ...state.fetchedByCommissionId, [commissionId]: true },
            latestUpdatedAtByCommissionId: {
                ...state.latestUpdatedAtByCommissionId,
                [commissionId]: newLatestUpdatedAt && newLatestUpdatedAt.getTime() > 0 ? newLatestUpdatedAt : null,
            },
        });
    }),

    on(ConstructionDiaryActionTypes.UpdateMany, (state, { Payload, commissionId, updateLatestUpdatedAt }) =>
        Payload.reduce((s, entity) => adapter.setOne(entity, { ...s }), {
            ...state,
            fetchedByCommissionId: { ...state.fetchedByCommissionId, [commissionId]: true },
            latestUpdatedAtByCommissionId: {
                ...state.latestUpdatedAtByCommissionId,
                [commissionId]: updateLatestUpdatedAt ? getLatestUpdatedAt(Payload, state.latestUpdatedAtByCommissionId[commissionId] ?? null) : state.latestUpdatedAtByCommissionId[commissionId],
            },
        }),
    ),
    on(ConstructionDiaryActionTypes.UpdateOne, (state, { Payload }) => adapter.setOne(Payload, { ...state })),
);

/* ############### */
/* ## SELECTORS ## */
/* ############### */

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const selectAllConstructionDiaries = selectAll;
export const selectConstructionDiaryEntities = selectEntities;
export const selectConstructionDiaryLatestUpdatedAtByCommissionId = (state: ConstructionDiaryState) => state.latestUpdatedAtByCommissionId;
export const selectConstructionDiariesFetchedByCommissionId = (state: ConstructionDiaryState) => state.fetchedByCommissionId
